import { useState, useEffect, useCallback } from 'react';
import { 
  Search, Eye, Ban, Lock, Unlock, ChevronLeft, ChevronRight, Filter, Download,
  User, Mail, Phone, MapPin, Calendar, Shield, Wallet, CreditCard, Gamepad2,
  AlertTriangle, History, FileText, Smartphone, Globe, Users as UsersIcon,
  DollarSign, TrendingUp, TrendingDown, Clock, Flag, Bell, Edit, Plus,
  X, Check, AlertCircle, XCircle, CheckCircle, MoreVertical, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { usersAPI, kycAPI, mobileUsersAPI } from '../lib/api';
import PlayerDetail from './PlayerDetail';
import { formatDateTime, formatCurrency, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

// Status Badge Component
const StatusBadge = ({ status, type = 'default' }) => {
  const colorMap = {
    active: 'badge-success',
    approved: 'badge-success',
    completed: 'badge-success',
    win: 'badge-success',
    blocked: 'badge-danger',
    rejected: 'badge-danger',
    failed: 'badge-danger',
    loss: 'badge-danger',
    suspended: 'badge-warning',
    pending: 'badge-warning',
    frozen: 'badge-info',
    processing: 'badge-info',
    drop: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorMap[status] || 'badge-neutral'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

// Risk Score Badge
const RiskBadge = ({ score, category }) => {
  const getColor = () => {
    if (score >= 80 || category === 'high') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (score >= 50 || category === 'medium') return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
  };
  
  return (
    <span className={`badge border ${getColor()}`}>
      {score} ({category})
    </span>
  );
};

// VIP Level Badge
const VIPBadge = ({ level }) => {
  const colors = [
    'bg-slate-500/20 text-slate-400',
    'bg-amber-500/20 text-amber-400',
    'bg-blue-500/20 text-blue-400',
    'bg-violet-500/20 text-violet-400',
    'bg-pink-500/20 text-pink-400',
    'bg-yellow-500/20 text-yellow-400',
  ];
  
  return (
    <span className={`badge ${colors[level] || colors[0]}`}>
      VIP {level}
    </span>
  );
};

// User Profile Detail Component
export const UserProfile = ({ userId, onBack }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [referrals, setReferrals] = useState({ referrals: [], total: 0 });
  const [kycDocs, setKycDocs] = useState([]);
  
  // Dialogs
  const [walletDialog, setWalletDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [noteDialog, setNoteDialog] = useState(false);
  const [riskDialog, setRiskDialog] = useState(false);
  const [limitDialog, setLimitDialog] = useState(false);
  
  // Form states
  const [walletAction, setWalletAction] = useState({ type: 'credit', wallet_type: 'real', amount: '', reason: '' });
  const [newStatus, setNewStatus] = useState({ status: '', reason: '' });
  const [newNote, setNewNote] = useState('');
  const [riskData, setRiskData] = useState({ score: 0, category: 'low', reason: '' });
  const [limitData, setLimitData] = useState({});
  
  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getById(userId);
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to fetch user');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [userId, onBack]);
  
  const fetchTabData = useCallback(async (tab) => {
    try {
      switch(tab) {
        case 'transactions':
          const txRes = await usersAPI.getTransactions(userId, { limit: 100 });
          setTransactions(txRes.data.transactions || txRes.data);
          break;
        case 'games':
          const gameRes = await usersAPI.getGameHistory(userId, { limit: 100 });
          setGameHistory(gameRes.data.games || gameRes.data);
          break;
        case 'logs':
          const logRes = await usersAPI.getAdminLogs(userId, { limit: 100 });
          setAdminLogs(logRes.data.logs || []);
          break;
        case 'devices':
          const devRes = await usersAPI.getDevices(userId);
          setDevices(devRes.data || []);
          const loginRes = await usersAPI.getLoginHistory(userId, { limit: 50 });
          setLoginHistory(loginRes.data.logins || []);
          break;
        case 'referrals':
          const refRes = await usersAPI.getReferrals(userId);
          setReferrals(refRes.data);
          break;
        case 'kyc':
          const kycRes = await usersAPI.getKyc(userId);
          setKycDocs(kycRes.data || []);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Failed to fetch tab data:', error);
    }
  }, [userId]);
  
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);
  
  useEffect(() => {
    if (user) {
      fetchTabData(activeTab);
    }
  }, [activeTab, user, fetchTabData]);
  
  const handleWalletAdjust = async () => {
    if (!walletAction.amount || !walletAction.reason) {
      toast.error('Amount and reason are required');
      return;
    }
    try {
      await usersAPI.adjustWallet({
        user_id: userId,
        amount: parseFloat(walletAction.amount),
        wallet_type: walletAction.wallet_type,
        action: walletAction.type,
        reason: walletAction.reason
      });
      toast.success('Wallet adjusted successfully');
      setWalletDialog(false);
      setWalletAction({ type: 'credit', wallet_type: 'real', amount: '', reason: '' });
      fetchUser();
    } catch (error) {
      toast.error('Failed to adjust wallet');
    }
  };
  
  const handleStatusChange = async () => {
    if (!newStatus.status || !newStatus.reason) {
      toast.error('Status and reason are required');
      return;
    }
    try {
      await usersAPI.changeStatus(userId, newStatus.status, newStatus.reason);
      toast.success('Status updated successfully');
      setStatusDialog(false);
      setNewStatus({ status: '', reason: '' });
      fetchUser();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Note is required');
      return;
    }
    try {
      await usersAPI.addNote(userId, newNote);
      toast.success('Note added');
      setNoteDialog(false);
      setNewNote('');
      fetchUser();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };
  
  const handleRiskUpdate = async () => {
    if (!riskData.reason) {
      toast.error('Reason is required');
      return;
    }
    try {
      await usersAPI.markRisk(userId, riskData.score, riskData.category, riskData.reason);
      toast.success('Risk assessment updated');
      setRiskDialog(false);
      fetchUser();
    } catch (error) {
      toast.error('Failed to update risk');
    }
  };
  
  const handleFreezeWallet = async () => {
    try {
      if (user.wallet_frozen) {
        await usersAPI.unfreezeWallet(userId);
        toast.success('Wallet unfrozen');
      } else {
        await usersAPI.freezeWallet(userId);
        toast.success('Wallet frozen');
      }
      fetchUser();
    } catch (error) {
      toast.error('Failed to update wallet status');
    }
  };
  
  const handleResponsibleGaming = async () => {
    try {
      await usersAPI.setResponsibleGaming(userId, limitData);
      toast.success('Responsible gaming limits updated');
      setLimitDialog(false);
      fetchUser();
    } catch (error) {
      toast.error('Failed to update limits');
    }
  };
  
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="user-profile">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-xl">
              {user.name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{user.name}</h2>
              <p className="text-sm text-muted-foreground font-mono">{user.username} • {user.id.slice(0, 8)}...</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={user.status} />
          <VIPBadge level={user.vip_level} />
          <RiskBadge score={user.risk_score} category={user.fraud_category} />
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setWalletDialog(true)} data-testid="adjust-wallet-btn">
          <DollarSign className="w-4 h-4 mr-1" /> Adjust Wallet
        </Button>
        <Button size="sm" variant="outline" onClick={handleFreezeWallet} data-testid="freeze-wallet-btn">
          {user.wallet_frozen ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
          {user.wallet_frozen ? 'Unfreeze' : 'Freeze'} Wallet
        </Button>
        <Button size="sm" variant="outline" onClick={() => setStatusDialog(true)} data-testid="change-status-btn">
          <Shield className="w-4 h-4 mr-1" /> Change Status
        </Button>
        <Button size="sm" variant="outline" onClick={() => setRiskDialog(true)} data-testid="update-risk-btn">
          <AlertTriangle className="w-4 h-4 mr-1" /> Update Risk
        </Button>
        <Button size="sm" variant="outline" onClick={() => setNoteDialog(true)} data-testid="add-note-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Note
        </Button>
        <Button size="sm" variant="outline" onClick={() => setLimitDialog(true)} data-testid="set-limits-btn">
          <Clock className="w-4 h-4 mr-1" /> Set Limits
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview"><User className="w-4 h-4 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="wallet"><Wallet className="w-4 h-4 mr-1" />Wallet</TabsTrigger>
          <TabsTrigger value="transactions"><CreditCard className="w-4 h-4 mr-1" />Transactions</TabsTrigger>
          <TabsTrigger value="games"><Gamepad2 className="w-4 h-4 mr-1" />Game History</TabsTrigger>
          <TabsTrigger value="risk"><AlertTriangle className="w-4 h-4 mr-1" />Risk & Fraud</TabsTrigger>
          <TabsTrigger value="kyc"><FileText className="w-4 h-4 mr-1" />KYC</TabsTrigger>
          <TabsTrigger value="responsible"><Shield className="w-4 h-4 mr-1" />Responsible Gaming</TabsTrigger>
          <TabsTrigger value="referrals"><UsersIcon className="w-4 h-4 mr-1" />Referrals</TabsTrigger>
          <TabsTrigger value="devices"><Smartphone className="w-4 h-4 mr-1" />Devices</TabsTrigger>
          <TabsTrigger value="logs"><History className="w-4 h-4 mr-1" />Admin Logs</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                Basic Information
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">User ID</span><span className="font-mono">{user.id.slice(0, 12)}...</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Username</span><span>{user.username}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Full Name</span><span>{user.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{user.email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Mobile</span><span>{user.mobile}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Country</span><span>{user.country}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Currency</span><span>{user.currency}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">DOB</span><span>{user.date_of_birth}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Age Verified</span><span>{user.age_verified ? '✓' : '✗'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDateTime(user.created_at)}</span></div>
              </div>
            </div>
            
            {/* Financial Summary */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary" />
                Financial Summary
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Wallet Balance</span><span className="text-emerald-400 font-mono">{formatCurrency(user.wallet_balance, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Bonus Balance</span><span className="text-amber-400 font-mono">{formatCurrency(user.bonus_balance, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Winnings</span><span className="font-mono">{formatCurrency(user.winnings_balance, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Locked Bonus</span><span className="font-mono">{formatCurrency(user.locked_bonus, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Withdrawable</span><span className="font-mono">{formatCurrency(user.withdrawable_amount, user.currency)}</span></div>
                <div className="border-t border-border pt-2 mt-2"></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Deposits</span><span className="font-mono">{formatCurrency(user.total_deposits, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Withdrawals</span><span className="font-mono">{formatCurrency(user.total_withdrawals, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Net P/L</span><span className={`font-mono ${user.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(user.net_profit, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Rake Paid</span><span className="font-mono">{formatCurrency(user.total_rake_paid, user.currency)}</span></div>
              </div>
            </div>
            
            {/* Account Status */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Account Status
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={user.status} /></div>
                <div className="flex justify-between"><span className="text-muted-foreground">KYC Status</span><StatusBadge status={user.kyc_status} /></div>
                <div className="flex justify-between"><span className="text-muted-foreground">VIP Level</span><VIPBadge level={user.vip_level} /></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Wallet Frozen</span><span>{user.wallet_frozen ? <XCircle className="w-4 h-4 text-red-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Deposits</span><span>{user.deposits_disabled ? <XCircle className="w-4 h-4 text-red-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Withdrawals</span><span>{user.withdrawals_disabled ? <XCircle className="w-4 h-4 text-red-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Under Investigation</span><span>{user.under_investigation ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Last Login</span><span>{formatDateTime(user.last_login)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Last Active</span><span>{formatDateTime(user.last_active)}</span></div>
              </div>
            </div>
            
            {/* Game Stats */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-primary" />
                Game Statistics
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Games Played</span><span className="font-mono">{user.total_games_played}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Winnings</span><span className="text-emerald-400 font-mono">{formatCurrency(user.total_winnings, user.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Losses</span><span className="text-red-400 font-mono">{formatCurrency(user.total_losses, user.currency)}</span></div>
              </div>
            </div>
            
            {/* Referral Info */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <UsersIcon className="w-4 h-4 text-primary" />
                Referral & Affiliate
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Referral Code</span><span className="font-mono">{user.referral_code}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Referred By</span><span className="font-mono">{user.referred_by || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Affiliate ID</span><span className="font-mono">{user.affiliate_id || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Referrals</span><span>{user.total_referrals}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Referral Earnings</span><span className="font-mono">{formatCurrency(user.referral_earnings, user.currency)}</span></div>
              </div>
            </div>
            
            {/* Admin Notes */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Admin Notes
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {user.notes && user.notes.length > 0 ? (
                  user.notes.map((note, i) => (
                    <p key={i} className="text-sm text-muted-foreground bg-muted p-2 rounded">{note}</p>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No notes added</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Wallet Tab */}
        <TabsContent value="wallet">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="stat-card">
              <p className="stat-label">Real Money</p>
              <p className="stat-value text-emerald-400">{formatCurrency(user.wallet_balance, user.currency)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Bonus Balance</p>
              <p className="stat-value text-amber-400">{formatCurrency(user.bonus_balance, user.currency)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Winnings</p>
              <p className="stat-value">{formatCurrency(user.winnings_balance, user.currency)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Withdrawable</p>
              <p className="stat-value text-blue-400">{formatCurrency(user.withdrawable_amount, user.currency)}</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4">Wallet Controls</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => { setWalletAction({...walletAction, type: 'credit'}); setWalletDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Credit Money
              </Button>
              <Button variant="outline" onClick={() => { setWalletAction({...walletAction, type: 'debit'}); setWalletDialog(true); }}>
                <TrendingDown className="w-4 h-4 mr-1" /> Debit Money
              </Button>
              <Button variant="outline" onClick={handleFreezeWallet}>
                {user.wallet_frozen ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                {user.wallet_frozen ? 'Unfreeze' : 'Freeze'} Wallet
              </Button>
            </div>
          </div>
        </TabsContent>
        
        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions found</TableCell></TableRow>
                ) : (
                  transactions.slice(0, 50).map((tx) => (
                    <TableRow key={tx.id} className="table-row">
                      <TableCell className="font-mono text-xs">{tx.reference_id || tx.id.slice(0, 8)}</TableCell>
                      <TableCell><span className="capitalize">{tx.type}</span></TableCell>
                      <TableCell className={`font-mono ${tx.type === 'deposit' || tx.type === 'bonus' || tx.type === 'winnings' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tx.type === 'withdrawal' || tx.type === 'rake' ? '-' : '+'}{formatCurrency(tx.amount, tx.currency)}
                      </TableCell>
                      <TableCell>{tx.payment_method || '-'}</TableCell>
                      <TableCell><StatusBadge status={tx.status} /></TableCell>
                      <TableCell className="text-sm">{formatDateTime(tx.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        
        {/* Game History Tab */}
        <TabsContent value="games">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Game ID</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead>Entry Fee</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Winnings</TableHead>
                  <TableHead>Rake</TableHead>
                  <TableHead>Opponents</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gameHistory.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No game history found</TableCell></TableRow>
                ) : (
                  gameHistory.slice(0, 50).map((game) => (
                    <TableRow key={game.id} className="table-row">
                      <TableCell className="font-mono text-xs">{game.game_id}</TableCell>
                      <TableCell className="capitalize">{game.variant?.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(game.entry_fee)}</TableCell>
                      <TableCell><StatusBadge status={game.result} /></TableCell>
                      <TableCell className="font-mono text-emerald-400">{formatCurrency(game.winning_amount)}</TableCell>
                      <TableCell className="font-mono text-red-400">{formatCurrency(game.rake_charged)}</TableCell>
                      <TableCell className="text-xs max-w-[150px] truncate">{game.opponents?.join(', ')}</TableCell>
                      <TableCell>{game.game_duration_minutes}m</TableCell>
                      <TableCell>
                        {game.suspicious_win_pattern && <AlertTriangle className="w-4 h-4 text-red-400 inline" title="Suspicious pattern" />}
                        {game.same_opponent_frequency > 5 && <UsersIcon className="w-4 h-4 text-amber-400 inline ml-1" title="Frequent opponent" />}
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(game.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        
        {/* Risk & Fraud Tab */}
        <TabsContent value="risk">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Risk Assessment</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Risk Score</span>
                  <span className={`text-2xl font-bold ${user.risk_score >= 80 ? 'text-red-400' : user.risk_score >= 50 ? 'text-amber-400' : 'text-emerald-400'}`}>{user.risk_score}/100</span>
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Fraud Category</span><span className="capitalize">{user.fraud_category}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Suspicious Flag</span><span>{user.suspicious_flag ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Account Anomalies</h3>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Multiple Accounts</span><span className={user.multiple_accounts_detected > 0 ? 'text-red-400' : ''}>{user.multiple_accounts_detected}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Shared Devices</span><span>{user.shared_device_count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Shared IPs</span><span>{user.shared_ip_count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">VPN Detected</span><span>{user.vpn_detected ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Geo Mismatch</span><span>{user.geo_mismatch ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Admin Actions</h3>
              <div className="flex flex-col gap-2">
                <Button size="sm" variant="destructive" onClick={() => setStatusDialog(true)}>
                  <Ban className="w-4 h-4 mr-1" /> Freeze Account
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setNewStatus({status: 'under_investigation', reason: ''}); setStatusDialog(true); }}>
                  <AlertTriangle className="w-4 h-4 mr-1" /> Mark Under Investigation
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRiskDialog(true)}>
                  <Flag className="w-4 h-4 mr-1" /> Update Risk Score
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* KYC Tab */}
        <TabsContent value="kyc">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">KYC Documents</h3>
              <StatusBadge status={user.kyc_status} />
            </div>
            {kycDocs.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
                No KYC documents found for this user
              </div>
            ) : (
              kycDocs.map((doc) => (
                <div key={doc.id} className="bg-card border border-border rounded-xl p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ') || 'Document'}</h4>
                      <p className="text-sm text-muted-foreground font-mono">{doc.document_number || doc.aadhaar_number || doc.pan_number}</p>
                    </div>
                    <StatusBadge status={doc.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {(doc.document_front_url || doc.aadhaar_front_url || doc.pan_front_url) && (
                      <img src={doc.document_front_url || doc.aadhaar_front_url || doc.pan_front_url} alt="Front" className="w-full h-32 object-cover rounded border border-border" />
                    )}
                    {(doc.document_back_url || doc.aadhaar_back_url) && (
                      <img src={doc.document_back_url || doc.aadhaar_back_url} alt="Back" className="w-full h-32 object-cover rounded border border-border" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
        
        {/* Responsible Gaming Tab */}
        <TabsContent value="responsible">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Current Limits</h3>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Daily Deposit Limit</span><span className="font-mono">{user.deposit_limit_daily ? formatCurrency(user.deposit_limit_daily) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Weekly Deposit Limit</span><span className="font-mono">{user.deposit_limit_weekly ? formatCurrency(user.deposit_limit_weekly) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Monthly Deposit Limit</span><span className="font-mono">{user.deposit_limit_monthly ? formatCurrency(user.deposit_limit_monthly) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Loss Limit</span><span className="font-mono">{user.loss_limit ? formatCurrency(user.loss_limit) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Session Limit</span><span>{user.session_limit_minutes ? `${user.session_limit_minutes} mins` : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Reality Check</span><span>{user.reality_check_minutes ? `Every ${user.reality_check_minutes} mins` : 'None'}</span></div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Exclusions</h3>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Self Exclusion</span><span>{user.self_exclusion_until ? formatDateTime(user.self_exclusion_until) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cool Off Until</span><span>{user.cool_off_until ? formatDateTime(user.cool_off_until) : 'None'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Addiction Flag</span><span>{user.gambling_addiction_flag ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle className="w-4 h-4 text-emerald-400" />}</span></div>
              </div>
              <Button className="mt-4 w-full" onClick={() => setLimitDialog(true)}>
                <Edit className="w-4 h-4 mr-1" /> Manage Limits
              </Button>
            </div>
          </div>
        </TabsContent>
        
        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="stat-card">
              <p className="stat-label">Referral Code</p>
              <p className="stat-value text-primary font-mono">{user.referral_code}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Total Referrals</p>
              <p className="stat-value">{referrals.total}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Total Earnings</p>
              <p className="stat-value text-emerald-400">{formatCurrency(referrals.total_earnings || user.referral_earnings)}</p>
            </div>
          </div>
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Deposits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.referrals.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No referrals found</TableCell></TableRow>
                ) : (
                  referrals.referrals.map((ref, i) => (
                    <TableRow key={i} className="table-row">
                      <TableCell>{ref.name}</TableCell>
                      <TableCell>{ref.email}</TableCell>
                      <TableCell>{formatDateTime(ref.created_at)}</TableCell>
                      <TableCell className="font-mono">{formatCurrency(ref.total_deposits)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        
        {/* Devices Tab */}
        <TabsContent value="devices">
          <div className="space-y-6">
            <div className="data-table-container">
              <h3 className="p-4 font-semibold">Device History</h3>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Device ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>App Version</TableHead>
                    <TableHead>First Seen</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead>Trusted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No device history</TableCell></TableRow>
                  ) : (
                    devices.map((dev) => (
                      <TableRow key={dev.id} className="table-row">
                        <TableCell className="font-mono text-xs">{dev.device_id}</TableCell>
                        <TableCell>{dev.device_type}</TableCell>
                        <TableCell>{dev.os_version}</TableCell>
                        <TableCell>{dev.app_version}</TableCell>
                        <TableCell className="text-sm">{formatDateTime(dev.first_seen)}</TableCell>
                        <TableCell className="text-sm">{formatDateTime(dev.last_seen)}</TableCell>
                        <TableCell>{dev.is_trusted ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            <div className="data-table-container">
              <h3 className="p-4 font-semibold">Login History</h3>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No login history</TableCell></TableRow>
                  ) : (
                    loginHistory.slice(0, 20).map((login) => (
                      <TableRow key={login.id} className="table-row">
                        <TableCell className="font-mono text-xs">{login.ip_address}</TableCell>
                        <TableCell className="font-mono text-xs">{login.device_id?.slice(0, 10)}</TableCell>
                        <TableCell>{login.location}</TableCell>
                        <TableCell>{login.success ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}</TableCell>
                        <TableCell className="text-sm">{formatDateTime(login.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        
        {/* Admin Logs Tab */}
        <TabsContent value="logs">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Admin</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No admin logs found</TableCell></TableRow>
                ) : (
                  adminLogs.map((log) => (
                    <TableRow key={log.id} className="table-row">
                      <TableCell>
                        <p className="font-medium">{log.admin_name}</p>
                        <p className="text-xs text-muted-foreground">{log.admin_role}</p>
                      </TableCell>
                      <TableCell className="capitalize">{log.action?.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-xs max-w-[300px] truncate">{JSON.stringify(log.details)}</TableCell>
                      <TableCell className="text-sm">{formatDateTime(log.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Wallet Adjustment Dialog */}
      <Dialog open={walletDialog} onOpenChange={setWalletDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{walletAction.type === 'credit' ? 'Credit' : 'Debit'} Wallet</DialogTitle>
            <DialogDescription>Adjust user's wallet balance. This action will be logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Wallet Type</label>
              <Select value={walletAction.wallet_type} onValueChange={(v) => setWalletAction({...walletAction, wallet_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="real">Real Money</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                  <SelectItem value="winnings">Winnings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Amount</label>
              <Input type="number" value={walletAction.amount} onChange={(e) => setWalletAction({...walletAction, amount: e.target.value})} placeholder="Enter amount" />
            </div>
            <div>
              <label className="text-sm font-medium">Reason (Mandatory)</label>
              <Textarea value={walletAction.reason} onChange={(e) => setWalletAction({...walletAction, reason: e.target.value})} placeholder="Enter reason for adjustment..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalletDialog(false)}>Cancel</Button>
            <Button onClick={handleWalletAdjust}>{walletAction.type === 'credit' ? 'Credit' : 'Debit'} Wallet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Status</DialogTitle>
            <DialogDescription>Change user account status. Reason is mandatory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">New Status</label>
              <Select value={newStatus.status} onValueChange={(v) => setNewStatus({...newStatus, status: v})}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="frozen">Frozen</SelectItem>
                  <SelectItem value="under_investigation">Under Investigation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Reason (Mandatory)</label>
              <Textarea value={newStatus.reason} onChange={(e) => setNewStatus({...newStatus, reason: e.target.value})} placeholder="Enter reason..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleStatusChange}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Admin Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Enter note..." rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Risk Update Dialog */}
      <Dialog open={riskDialog} onOpenChange={setRiskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Risk Assessment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Risk Score (0-100)</label>
              <Input type="number" min="0" max="100" value={riskData.score} onChange={(e) => setRiskData({...riskData, score: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="text-sm font-medium">Fraud Category</label>
              <Select value={riskData.category} onValueChange={(v) => setRiskData({...riskData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Textarea value={riskData.reason} onChange={(e) => setRiskData({...riskData, reason: e.target.value})} placeholder="Enter reason..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRiskDialog(false)}>Cancel</Button>
            <Button onClick={handleRiskUpdate}>Update Risk</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Responsible Gaming Limits Dialog */}
      <Dialog open={limitDialog} onOpenChange={setLimitDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Responsible Gaming Limits</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium">Daily Deposit Limit</label>
              <Input type="number" value={limitData.deposit_limit_daily || ''} onChange={(e) => setLimitData({...limitData, deposit_limit_daily: parseFloat(e.target.value) || null})} placeholder="No limit" />
            </div>
            <div>
              <label className="text-sm font-medium">Weekly Deposit Limit</label>
              <Input type="number" value={limitData.deposit_limit_weekly || ''} onChange={(e) => setLimitData({...limitData, deposit_limit_weekly: parseFloat(e.target.value) || null})} placeholder="No limit" />
            </div>
            <div>
              <label className="text-sm font-medium">Monthly Deposit Limit</label>
              <Input type="number" value={limitData.deposit_limit_monthly || ''} onChange={(e) => setLimitData({...limitData, deposit_limit_monthly: parseFloat(e.target.value) || null})} placeholder="No limit" />
            </div>
            <div>
              <label className="text-sm font-medium">Loss Limit</label>
              <Input type="number" value={limitData.loss_limit || ''} onChange={(e) => setLimitData({...limitData, loss_limit: parseFloat(e.target.value) || null})} placeholder="No limit" />
            </div>
            <div>
              <label className="text-sm font-medium">Session Limit (minutes)</label>
              <Input type="number" value={limitData.session_limit_minutes || ''} onChange={(e) => setLimitData({...limitData, session_limit_minutes: parseInt(e.target.value) || null})} placeholder="No limit" />
            </div>
            <div>
              <label className="text-sm font-medium">Reality Check (minutes)</label>
              <Input type="number" value={limitData.reality_check_minutes || ''} onChange={(e) => setLimitData({...limitData, reality_check_minutes: parseInt(e.target.value) || null})} placeholder="No reminder" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitDialog(false)}>Cancel</Button>
            <Button onClick={handleResponsibleGaming}>Save Limits</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Users Page Component
export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        search: filters.search || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
      };
      const response = await mobileUsersAPI.getAll(params);
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch players');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('No players selected');
      return;
    }
    try {
      for (const userId of selectedUsers) {
        if (action === 'block') await mobileUsersAPI.ban(userId, 'Banned by admin');
        if (action === 'unblock') await mobileUsersAPI.unban(userId);
      }
      toast.success(`Updated ${selectedUsers.length} players`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };
  
  const handleExport = () => {
    const csv = users.map(u => `${u.user_id},${u.name || ''},${u.phone || ''},${u.total_balance || 0},${u.is_banned ? 'banned' : 'active'}`).join('\n');
    const blob = new Blob([`ID,Name,Phone,Balance,Status\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'players.csv';
    a.click();
    toast.success('Export started');
  };
  
  const totalPages = Math.ceil(total / limit);
  const activeOnPage = users.filter((u) => !u.is_banned).length;
  const bannedOnPage = users.filter((u) => u.is_banned).length;
  
  if (selectedUser) {
    return <PlayerDetail user={selectedUser} onBack={() => { setSelectedUser(null); fetchUsers(); }} />;
  }
  
  return (
    <div data-testid="users-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Players</h1>
        <p className="page-subtitle">Search app accounts, balances, and ban players who are live</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <p className="stat-label">Players</p>
          <p className="stat-value">{total}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Active on this page</p>
          <p className="stat-value text-emerald-400">{activeOnPage}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Banned on this page</p>
          <p className="stat-value text-red-400">{bannedOnPage}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Balances on this page</p>
          <p className="stat-value text-amber-400">{formatCurrency(users.reduce((sum, u) => sum + Number(u.total_balance || 0), 0))}</p>
        </div>
      </div>
      
      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search name, phone, or user id"
              value={filters.search}
              onChange={(e) => { setPage(0); setFilters({...filters, search: e.target.value}); }}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>
        
        <Select value={filters.status} onValueChange={(v) => { setPage(0); setFilters({...filters, status: v}); }}>
          <SelectTrigger className="w-36" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All players</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={handleExport} data-testid="export-btn">
          <Download className="w-4 h-4 mr-1" /> Export
        </Button>
        
        <Button variant="outline" onClick={fetchUsers} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-card border border-border rounded-lg">
          <span className="text-sm text-muted-foreground">{selectedUsers.length} selected</span>
          <Button size="sm" variant="destructive" onClick={() => handleBulkAction('block')}>Ban</Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction('unblock')}>Unban</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedUsers([])}>Clear</Button>
        </div>
      )}
      
      {/* Users Table */}
      <div className="data-table-container">
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onCheckedChange={(checked) => setSelectedUsers(checked ? users.map(u => u.user_id) : [])}
                />
              </TableHead>
              <TableHead>Player</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Deposit</TableHead>
              <TableHead>Winnings</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No players found</TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.user_id} className="table-row" data-testid={`user-row-${user.user_id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedUsers.includes(user.user_id)}
                      onCheckedChange={(checked) => setSelectedUsers(checked ? [...selectedUsers, user.user_id] : selectedUsers.filter(id => id !== user.user_id))}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                        {(user.name || user.phone || '?').charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{user.name || 'Unnamed'}</p>
                        <p className="text-xs text-muted-foreground font-mono">{user.user_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{user.phone || '—'}</TableCell>
                  <TableCell className="font-mono text-emerald-400">{formatCurrency(user.deposit_balance || 0)}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(user.winnings_balance || 0)}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(user.total_balance || 0)}</TableCell>
                  <TableCell><StatusBadge status={user.is_banned ? 'blocked' : 'active'} /></TableCell>
                  <TableCell className="text-sm">{formatDateTime(user.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelectedUser(user)} data-testid={`view-user-${user.user_id}`}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
